/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Button, DatePicker, message, Popconfirm, Table } from 'antd';
import { DeleteOutlined, PlusOutlined, SaveTwoTone } from '@ant-design/icons';
import dayjs from 'dayjs';
import { budgetsApi, BudgetRequest } from '@/services/action-plan/budget';
// import { useRouter } from 'next/router';
import { usePrivateStore } from '@/store/usePrivateStore';
import TextArea from 'antd/es/input/TextArea';
import { UploadModal } from '@/components/common/ActionPlan/CongViecDuAn/Right/Table/Modals';
import { usePermission } from '@/hooks/usePermission';
import FormattedDecimalInput from '@/components/common/FormattedDecimalInput';

interface ColumnItem {
  id: number;
  dateTime: string;
  budget: number | null;
  note: string;
  document: number;
}

interface TableBudgetProps {
  data: any;
  kanbans: any[]; // Danh sách Kanbans từ data.duan.kanbans
  onSaveNganSachSuccess?: () => void;
}

// Reference ổn định để React không remount thead/tbody mỗi render (gây mất focus khi nhập)
const TABLE_COMPONENTS = {
  header: {
    wrapper: (props: any) => (
      <thead className="bg-gray-100 border-b border-gray-300 text-xs">{props.children}</thead>
    ),
  },
  body: {
    wrapper: (props: any) => <tbody className="text-xs">{props.children}</tbody>,
  },
};

const TableBudget: React.FC<TableBudgetProps> = ({ data, kanbans, onSaveNganSachSuccess }) => {
  // const router = useRouter();
  // const { params } = router.query;
  // const paramsArray = Array.isArray(params) ? params : [];

  // const idProject = paramsArray[3] || 'undefined';

  const { giaiDoan, roleGroup } = usePrivateStore();
  const idPhuTrachSession = usePrivateStore((s) => s.getRequestHeader().selectTree?.idSelected || '');
  const [columns, setColumns] = useState<ColumnItem[]>([]);
  const [newColumnName, setNewColumnName] = useState<string>('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedRecordForUpload, setSelectedRecordForUpload] = useState<ColumnItem | null>(null);
  const [documentFiles, setDocumentFiles] = useState<string[]>([]);
  const [savingBudgetId, setSavingBudgetId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const buoc8Permission = usePermission('buoc9'); // là bước action plan - buoc9 - trong sdk
  const canCreateBudget = Boolean(buoc8Permission?.create || buoc8Permission?.fullcontrol);
  const canUpdateBudget = Boolean(buoc8Permission?.update || buoc8Permission?.fullcontrol);
  const canDeleteBudget = Boolean(buoc8Permission?.delete || buoc8Permission?.fullcontrol);

  const hasChild = data.duAnCongViec.childs.length > 0;

  const getListBudget = async () => {
    setLoading(true);
    try {
      const response: any = await budgetsApi.getList({
        id: data.duAnCongViec.congViec.id || '',
        idGiaiDoan: giaiDoan.id || '',
        idPhuTrach: idPhuTrachSession || '',
        parentID: '',
      });
      if (response.success) {
        setColumns(
          response.data.listNganSachs.map((item: any) => ({
            id: item.id,
            dateTime: item.ngayCapNhat || item.ngayTao || '',
            budget: item.nganSachThucTe || 0,
            note: item.ghiChu || '',
            document: item.soTaiLieu || 0,
          }))
        );
      } else {
        setColumns([]);
        if (response.message) {
          message.error(response.message);
        }
      }
    } catch (error: any) {
      console.error('Get list budget error:', error);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  const saveBudget = async (record: ColumnItem) => {
    if (!canUpdateBudget) {
      message.error('Bạn không có quyền cập nhật ngân sách');
      return;
    }
    setSavingBudgetId(record.id);
    try {
      const payload = {
        congViec: {
          id: data.duAnCongViec.congViec.id || '',
          site: roleGroup?.site || '',
          kanbans: kanbans,
          parentID: data.duAnCongViec.congViec.parentID || '',
        },
        listNganSachs: [
          {
            id: String(record.id),
            site: roleGroup?.site || '',
            kyBaoCao: record.dateTime || '',
            nganSachThucTe: record.budget || 0,
            ghiChu: record.note || '',
            soTaiLieu: record.document || 0,

          },
        ],
        listNganSachChilds: [],
      };
      const response: any = await budgetsApi.save(payload);
      if (response.success) {
        message.success('Lưu thành công');
        await getListBudget();
        onSaveNganSachSuccess?.();
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi lưu ngân sách');
    } finally {
      setSavingBudgetId(null);
    }
  };

  const deleteBudget = async (id: number) => {
    if (!canDeleteBudget) {
      message.error('Bạn không có quyền xóa ngân sách');
      return;
    }
    try {
      const payload: BudgetRequest = {
        id: String(id),
      };
      const response: any = await budgetsApi.delete(payload);
      if (response.success) {
        message.success('Xóa ngân sách thành công');
        setColumns((prev) => (prev || []).filter((col) => col.id !== id));
        await getListBudget();
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi xóa ngân sách');
    }
  };

  useEffect(() => {
    getListBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddColumn = () => {
    if (!canCreateBudget) {
      message.error('Bạn không có quyền thêm ngân sách');
      return;
    }
    const maxId = columns.length > 0 ? Math.max(...columns.map((col) => col.id)) : 0;
    const newId = maxId > 0 ? maxId + 1 : Date.now();
    const newCol: ColumnItem = {
      id: newId,
      dateTime: dayjs().format('DD/MM/YYYY'),
      budget: null,
      note: '',
      document: 0,
    };
    setColumns((prev) => [...(prev || []), newCol]);
    setNewColumnName('');
  };

  const handleBudgetChange = (id: number, value: number | null) => {
    setColumns((prev) => (prev || []).map((col) => (col.id === id ? { ...col, budget: value || 0 } : col)));
  };

  const handleDateTimeChange = (id: number, date: dayjs.Dayjs | null) => {
    setColumns((prev) =>
      (prev || []).map((col) => (col.id === id ? { ...col, dateTime: date ? date.format('DD/MM/YYYY') : '' } : col))
    );
  };

  const handleNoteChange = (id: number, value: string) => {
    setColumns((prev) => (prev || []).map((col) => (col.id === id ? { ...col, note: value } : col)));
  };

  const handleUploadModalSave = (files: string[]) => {
    if (!selectedRecordForUpload) return;

    const updated = columns.map((item) =>
      item.id === selectedRecordForUpload.id ? { ...item, document: files.length } : item
    );
    setColumns(updated);
    setUploadModalVisible(false);
    setSelectedRecordForUpload(null);
    setDocumentFiles([]);
    message.success('Đã cập nhật tài liệu');
  };

  const tableColumns: any = [
    {
      title: 'Kỳ báo cáo',
      dataIndex: 'dateTime',
      align: 'left',
      render: (_: string, record: ColumnItem) => {
        let dateValue: dayjs.Dayjs | null = null;
        if (record.dateTime && record.dateTime.trim()) {
          dateValue = dayjs(record.dateTime);
          if (!dateValue.isValid()) {
            dateValue = dayjs(record.dateTime, 'DD/MM/YYYY', true);
          }
          if (!dateValue.isValid()) {
            dateValue = dayjs(record.dateTime);
          }
        }
        const isValidDate = dateValue && dateValue.isValid();
        return (
          <DatePicker
            value={isValidDate ? dateValue : null}
            format="DD/MM/YYYY"
            onChange={(date) => handleDateTimeChange(record.id, date)}
            size="middle"
            disabled={!canUpdateBudget}
          />
        );
      },
    },
    {
      title: 'Ngân sách',
      dataIndex: 'budget',
      align: 'left',
      render: (item: number | null, record: ColumnItem) => (
        <FormattedDecimalInput
          value={item}
          disabled={!canUpdateBudget}
          onValueCommit={(numValue) => handleBudgetChange(record.id, numValue)}
          className="font-semibold cvda-ngan-sach-input"
          style={{
            textAlign: 'left',
            // border: 'none',
            backgroundColor: 'white',
            boxShadow: 'none',
            outline: 'none',
            width: '100%',
            margin: 0,
          }}
        />
      ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      align: 'left',
      render: (item: string, record: ColumnItem) => (
        <TextArea
          value={item || ''}
          onChange={(e) => handleNoteChange(record.id, e.target.value)}
          autoSize={{ minRows: 1 }}
          className="!h-[30px]"
          disabled={!canUpdateBudget}
        />
      ),
    },
    {
      title: 'Tài liệu',
      dataIndex: 'document',
      align: 'left',
      // render: (item: number, record: ColumnItem) => (
      //   <Alert
      //     message={`Có ${item} file tài liệu`}
      //     type="success"
      //     style={{ cursor: 'pointer', fontSize: '10px', padding: '5px' }}
      //     onClick={() => handleOpenUploadModal(record)}
      //   />
      // ),
      render: (files: string, record: ColumnItem) => {
        const fileArray = typeof files === 'string' ? files.split(',').filter((f) => f.trim()) : [];
        const fileCount = fileArray.length;
        return (
          <Button
            size="small"
            style={{
              cursor: 'pointer',
              backgroundColor: '#5cb85c',
              color: '#fff',
              fontWeight: 'bold',
              border: 'none',
            }}
            onClick={() => {
              setSelectedRecordForUpload(record);
              setDocumentFiles(fileArray);
              setUploadModalVisible(true);
            }}
            disabled={!canUpdateBudget}
          >
            {fileCount > 0 ? `Có ${fileCount} file tài liệu` : 'Không có file'}
          </Button>
        );
      },
    },
    {
      title: !hasChild && (
        <Button
          type="text"
          icon={<PlusOutlined />}
          className="text-white bg-blue-500 hover:bg-blue-600 hover:text-white border-none rounded-full"
          onClick={handleAddColumn}
          disabled={newColumnName.trim() !== '' || !canCreateBudget}
        />
      ),
      key: 'actions',
      width: '85px',
      align: 'center',
      render: (_: any, record: ColumnItem) => (
        <>
          <Button
            type="text"
            icon={<SaveTwoTone />}
            onClick={() => saveBudget(record)}
            loading={savingBudgetId === record.id}
            disabled={savingBudgetId === record.id || !canUpdateBudget}
          />
          <Popconfirm
            title="Xác nhận xóa ngân sách"
            description="Bạn có chắc chắn muốn xóa ngân sách này?"
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteBudget(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} disabled={!canDeleteBudget} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={columns}
        columns={tableColumns}
        rowKey="id"
        pagination={false}
        loading={loading}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "bg-gray-50" : "bg-white"
        }
        components={TABLE_COMPONENTS}
      />

      <UploadModal
        visible={uploadModalVisible}
        selectedRecord={selectedRecordForUpload}
        documentFiles={documentFiles}
        onCancel={() => {
          setUploadModalVisible(false);
          setSelectedRecordForUpload(null);
          setDocumentFiles([]);
        }}
        onSave={handleUploadModalSave}
      />
    </>
  );
};

export default TableBudget;
